// Landing page for Audio Annotation Tool (for annotating emergency vehicle sounds such as sirens, horns, etc. and maybe more in the future)

import { FC, useEffect } from 'react';
import {
  ProfileMenuPortal,
  TitlePortal,
} from 'packages/main/components/Layouts/portals';
import { useRecoilState, useResetRecoilState } from 'recoil';
import {
  audioExtractionTokenAtom,
  curAnnotationTypesAtom,
  curSelectedRegionAtom,
  curTimeAtom,
  durationAtom,
  isPlayingAtom,
} from 'packages/main/components/audio-tool/atoms';
import { devLog } from 'packages/main/components/audio-tool/utils';
import { SWRConfig } from 'swr';
import { AudioToolMain } from 'packages/main/components/audio-tool/audio-tool-main';
import { isLocalhost } from 'packages/main/serviceWorkerRegistration';
import { AudioAppBarTaskMgmt } from 'packages/main/components/audio-tool/audio-app-bar-task-mgmt';
import { useParams } from 'react-router-dom';
import { TaskModes } from 'packages/main/components/audio-tool/audio-tool-types';
import { useNonNullQueryParams } from 'packages/lib/utils/hooks/use-query';
import { RecoilURLSync } from 'recoil-sync';

const AudioToolComp: FC<React.PropsWithChildren<unknown>> = () => {
  const { mode: modeFromURL } = useParams<{ mode?: string }>();
  const urlParamsObj = useNonNullQueryParams<{
    audioExtractionToken: string | undefined;
  }>();
  const [audioExtractionToken, setAudioExtractionToken] = useRecoilState(
    audioExtractionTokenAtom
  );
  const resetAudioExtractionTokenAtom = useResetRecoilState(
    audioExtractionTokenAtom
  );
  const resetCurTimeAtom = useResetRecoilState(curTimeAtom);
  const resetDurationAtom = useResetRecoilState(durationAtom);
  const resetIsPlayingAtom = useResetRecoilState(isPlayingAtom);
  const resetCurSelectedRegionsAtom = useResetRecoilState(
    curSelectedRegionAtom
  );
  const resetCurAnnotationTypesAtom = useResetRecoilState(
    curAnnotationTypesAtom
  );

  // RECOIL SYNC BUG WORKAROUND: When the user clicks a link on the Sample Group page that takes him to the audio tool, and the URL already has an audioExtractionToken param,
  // for some reason Recoil's syncEffect() does NOT populate the atom with that value. So we work around that here.
  useEffect(() => {
    if (
      urlParamsObj.audioExtractionToken &&
      urlParamsObj.audioExtractionToken.length > 0 &&
      audioExtractionToken.length === 0
    )
      setAudioExtractionToken(urlParamsObj.audioExtractionToken);
  }, [
    urlParamsObj.audioExtractionToken,
    audioExtractionToken,
    setAudioExtractionToken,
  ]);

  useEffect(() => {
    return () => {
      devLog('Shutting down audio tool: resetting Recoil Atoms.');

      // Note: on localhost dev, clearing this atom causes the audioExtractionToken to be cleared from the URL when hot reloading is done after source code is changed.
      // We do still need to clear this atom on quit, so that it is not added to the URL of other apps that use Recoil's URL syncing, such as the Scene Hunting page.
      if (!isLocalhost) {
        resetAudioExtractionTokenAtom();
      }

      // Reset some atoms on quit, so next use of the tool will be in normal default state.
      resetCurTimeAtom();
      resetDurationAtom();
      resetIsPlayingAtom();
      resetCurSelectedRegionsAtom();
      resetCurAnnotationTypesAtom();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const modeText =
    modeFromURL === TaskModes.Label
      ? ' - Labeling Mode'
      : modeFromURL === TaskModes.QA
      ? ' - QA Mode'
      : '';

  return (
    <>
      <SWRConfig
        value={{
          suspense: false,
        }}
      >
        <ProfileMenuPortal />
        <TitlePortal text={'Audio Annotation Tool' + modeText} />
        <AudioAppBarTaskMgmt />
        <AudioToolMain />
      </SWRConfig>
    </>
  );
};

export const AudioTool: FC = () => (
  <RecoilURLSync
    location={{ part: 'queryParams' }}
    serialize={String}
    deserialize={(value: string): string | number =>
      isNaN(Number(value)) || value.length === 0 ? value : parseFloat(value)
    }
  >
    <AudioToolComp />
  </RecoilURLSync>
);

export default AudioTool;
